import { useCallback, useRef, useState } from 'react'
import { HISTORY_LIMIT } from '../constants/layout'

export interface HistoryApi {
  push: (snapshot: ImageData) => void
  undo: () => ImageData | null
  redo: () => ImageData | null
  reset: () => void
  canUndo: boolean
  canRedo: boolean
}

export function useHistory(limit: number = HISTORY_LIMIT): HistoryApi {
  const stackRef = useRef<ImageData[]>([])
  const indexRef = useRef(-1)
  const [counters, setCounters] = useState({ u: 0, r: 0 })

  const recompute = useCallback(() => {
    setCounters({
      u: indexRef.current,
      r: stackRef.current.length - 1 - indexRef.current,
    })
  }, [])

  const push = useCallback(
    (snapshot: ImageData) => {
      const trimmed = stackRef.current.slice(0, indexRef.current + 1)
      trimmed.push(snapshot)
      while (trimmed.length > limit) trimmed.shift()
      stackRef.current = trimmed
      indexRef.current = trimmed.length - 1
      recompute()
    },
    [limit, recompute],
  )

  const undo = useCallback((): ImageData | null => {
    if (indexRef.current <= 0) return null
    indexRef.current--
    recompute()
    return stackRef.current[indexRef.current] ?? null
  }, [recompute])

  const redo = useCallback((): ImageData | null => {
    if (indexRef.current >= stackRef.current.length - 1) return null
    indexRef.current++
    recompute()
    return stackRef.current[indexRef.current] ?? null
  }, [recompute])

  const reset = useCallback(() => {
    stackRef.current = []
    indexRef.current = -1
    recompute()
  }, [recompute])

  return {
    push,
    undo,
    redo,
    reset,
    canUndo: counters.u > 0,
    canRedo: counters.r > 0,
  }
}
